export const DEFAULT_SCAN_CORNERS = [
  { x: 0.08, y: 0.08 },
  { x: 0.92, y: 0.08 },
  { x: 0.92, y: 0.92 },
  { x: 0.08, y: 0.92 },
];

export const FULL_PHOTO_CORNERS = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

export const SCAN_WARP_DPI = 144;
const LETTER_WIDTH_INCHES = 8.5;
const LETTER_HEIGHT_INCHES = 11;

const solveLinearSystem = (matrix, vector) => {
  const n = vector.length;
  const rows = matrix.map((row, index) => [...row, vector[index]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(rows[row][col]) > Math.abs(rows[pivot][col])) pivot = row;
    }
    if (Math.abs(rows[pivot][col]) < 1e-10) return null;
    [rows[col], rows[pivot]] = [rows[pivot], rows[col]];
    const divisor = rows[col][col];
    for (let j = col; j <= n; j += 1) rows[col][j] /= divisor;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = rows[row][col];
      for (let j = col; j <= n; j += 1) {
        rows[row][j] -= factor * rows[col][j];
      }
    }
  }
  return rows.map((row) => row[n]);
};

export const homographyFromQuads = (source, dest) => {
  const matrix = [];
  const vector = [];
  for (let i = 0; i < 4; i += 1) {
    const { x, y } = dest[i];
    const u = source[i].x;
    const v = source[i].y;
    matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    vector.push(u);
    matrix.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    vector.push(v);
  }
  return solveLinearSystem(matrix, vector);
};

export const applyHomography = (homography, x, y) => {
  if (!homography) return { x: 0, y: 0 };
  const w = homography[6] * x + homography[7] * y + 1;
  if (Math.abs(w) < 1e-10) return { x: 0, y: 0 };
  return {
    x: (homography[0] * x + homography[1] * y + homography[2]) / w,
    y: (homography[3] * x + homography[4] * y + homography[5]) / w,
  };
};

export const cornersToPixels = (corners, width, height) =>
  (corners || DEFAULT_SCAN_CORNERS).map((corner) => ({
    x: Number(corner.x) * width,
    y: Number(corner.y) * height,
  }));

const sampleBilinear = (data, width, height, x, y) => {
  if (x < 0 || y < 0 || x >= width - 1 || y >= height - 1) {
    return [255, 255, 255, 255];
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const dx = x - x0;
  const dy = y - y0;
  const index = (px, py) => (py * width + px) * 4;
  const mix = (a, b, t) => a + (b - a) * t;
  const i00 = index(x0, y0);
  const i10 = index(x0 + 1, y0);
  const i01 = index(x0, y0 + 1);
  const i11 = index(x0 + 1, y0 + 1);
  return [0, 1, 2, 3].map((channel) =>
    mix(
      mix(data[i00 + channel], data[i10 + channel], dx),
      mix(data[i01 + channel], data[i11 + channel], dx),
      dy
    )
  );
};

export const warpImageToLetter = (image, corners = DEFAULT_SCAN_CORNERS) => {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) {
    throw new Error("That photo could not be read.");
  }
  const destWidth = Math.round(LETTER_WIDTH_INCHES * SCAN_WARP_DPI);
  const destHeight = Math.round(LETTER_HEIGHT_INCHES * SCAN_WARP_DPI);
  const source = cornersToPixels(corners, sourceWidth, sourceHeight);
  const dest = [
    { x: 0, y: 0 },
    { x: destWidth - 1, y: 0 },
    { x: destWidth - 1, y: destHeight - 1 },
    { x: 0, y: destHeight - 1 },
  ];
  const homography = homographyFromQuads(source, dest);
  if (!homography) {
    throw new Error("Spread the four corners so they match the page.");
  }
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = sourceWidth;
  sourceCanvas.height = sourceHeight;
  const sourceCtx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  sourceCtx.drawImage(image, 0, 0);
  const sourcePixels = sourceCtx.getImageData(0, 0, sourceWidth, sourceHeight).data;

  const destCanvas = document.createElement("canvas");
  destCanvas.width = destWidth;
  destCanvas.height = destHeight;
  const destCtx = destCanvas.getContext("2d");
  const destImage = destCtx.createImageData(destWidth, destHeight);
  const destPixels = destImage.data;

  for (let y = 0; y < destHeight; y += 1) {
    for (let x = 0; x < destWidth; x += 1) {
      const mapped = applyHomography(homography, x, y);
      const sample = sampleBilinear(
        sourcePixels,
        sourceWidth,
        sourceHeight,
        mapped.x,
        mapped.y
      );
      const offset = (y * destWidth + x) * 4;
      destPixels[offset] = sample[0];
      destPixels[offset + 1] = sample[1];
      destPixels[offset + 2] = sample[2];
      destPixels[offset + 3] = sample[3];
    }
  }
  destCtx.putImageData(destImage, 0, 0);
  return destCanvas;
};

export const canvasToObjectUrl = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not build the page photo."));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      "image/jpeg",
      0.9
    );
  });

export const loadOrientedImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof createImageBitmap === "function") {
          try {
            const bitmap = await createImageBitmap(file, {
              imageOrientation: "from-image",
            });
            const canvas = document.createElement("canvas");
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext("2d").drawImage(bitmap, 0, 0);
            bitmap.close();
            URL.revokeObjectURL(url);
            const oriented = await canvasToObjectUrl(canvas);
            const ready = new Image();
            ready.onload = () => resolve({ url: oriented, image: ready });
            ready.onerror = () => resolve({ url: oriented, image: ready });
            ready.src = oriented;
            return;
          } catch (err) {
            // Fall through and use the file as-is.
          }
        }
        resolve({ url, image });
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Use a JPG or PNG photo of the blank check."));
    };
    image.src = url;
  });
